'use client';

import {
	SearchFacetsDataBGOV,
	SearchFacetsDataOP,
} from '@/globals/dataCollector/searchDataCollector/types/searchData.types';
import { SearchState } from '@/globals/dataCollector/searchDataCollector/types/searchParams.types';
import { DOPLink } from '@/uiComposites/interactive/DOPLink';
import { getSearchURL } from '@/globals/dataCollector/searchDataCollector/functions/getSearchURL';
import { getProjectFacets } from '@/globals/dataCollector/searchDataCollector/config/searchFacets.config';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { ScrollInlineWithSmallButtons } from '@/uiComposites/layout/scrollInline/ScrollInlineWithSmallButtons';
import { box } from '@/uiPrimitives/layout/box';
import { focusBox } from '@/uiPrimitives/base/focus.css';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { translateInline } from '@/globals/translate/translateInline';
import { KeyboardEvent, useRef } from 'react';

export const searchFacetTranslations = {
	all: translateInline({
		en: 'All',
		nl: 'Alle',
	}),
	forum: translateInline({ en: 'Community', nl: 'Forum' }),
	regulations: translateInline({ en: 'Regulations', nl: 'Wetten en regels' }),
	subsidies: translateInline({ en: 'Subsidies', nl: 'Subsidies' }),
	amendments: translateInline({ en: 'Amendments', nl: 'Wetswijzigingen' }),
};

export const SearchFacets = ({
	searchData,
	searchState,
}: {
	searchData: SearchFacetsDataBGOV | SearchFacetsDataOP;
	searchState: SearchState;
}) => {
	const facets = getProjectFacets();
	const activeFacetIndex = facets.findIndex(
		({ slug }) => slug === searchState.facetSlug,
	);
	const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);

	const focusTabAtIndex = (index: number) => {
		const total = facets.length;

		const normalizedIndex = ((index % total) + total) % total;
		tabRefs.current[normalizedIndex]?.focus();
	};

	const onFacetLinkKeyDown = (
		event: KeyboardEvent<HTMLAnchorElement>,
		index: number,
	) => {
		switch (event.key) {
			case 'ArrowRight':
			case 'ArrowDown': {
				event.preventDefault();
				focusTabAtIndex(index + 1);
				return;
			}
			case 'ArrowLeft':
			case 'ArrowUp': {
				event.preventDefault();
				focusTabAtIndex(index - 1);
				return;
			}
			case 'Home': {
				event.preventDefault();
				focusTabAtIndex(0);
				return;
			}
			case 'End': {
				event.preventDefault();
				focusTabAtIndex(facets.length - 1);
				return;
			}
			case ' ': {
				event.preventDefault();
				event.currentTarget.click();
				return;
			}
		}
	};

	return (
		<ScrollInlineWithSmallButtons>
			<nav
				aria-label={translateInline({
					en: 'Search categories',
					nl: 'Zoekcategorieen',
				})}
			>
				<ul role="tablist" className={classes(shelf({}))}>
					{facets.map(({ slug }, index) => {
						const url = getSearchURL({
							...searchState,
							facetSlug: slug,
							page: 1,
						});
						const isActiveFacet = slug === searchState.facetSlug;
						const isFocusableFacet =
							activeFacetIndex === -1 ? index === 0 : isActiveFacet;
						return (
							<li role="presentation" key={slug}>
								<DOPLink
									id={`search-facet-tab-${slug}`}
									role="tab"
									aria-selected={isActiveFacet}
									aria-controls="search-results-panel"
									tabIndex={isFocusableFacet ? 0 : -1}
									data-link-type="searchFacet"
									aria-current={isActiveFacet ? 'page' : undefined}
									href={url}
									scroll={false}
									onKeyDown={(event) => {
										onFacetLinkKeyDown(event, index);
									}}
									ref={(node) => {
										tabRefs.current[index] = node;
										// prevent legacy behaviour: https://react.dev/reference/react-dom/components/common#:~:text=React%2019%20added,a%20future%20version
										return () => {};
									}}
									className={classes(
										box({
											paddingBlock: '1 | h3',
											paddingInline: '1 | h3',
											backgroundColor: isActiveFacet
												? 'negative'
												: 'transparent',
										}),
										textStyle({
											color: 'positive',
											capSize: '0 | p',
										}),
									)}
									{...addTestSelector(`uiSearchTabRadio-${slug}`)}
								>
									<span
										className={classes(
											focusBox,
											box({
												paddingBlock: 'negative 0 | p',
											}),
										)}
									>
										<span
											className={classes(
												textBlock({
													textDecorationLine: 'none',
												}),
											)}
										>
											{searchFacetTranslations[slug]}
											{' ('}
											{searchData[slug as keyof typeof searchData]?.total}
											{')'}
										</span>
									</span>
								</DOPLink>
							</li>
						);
					})}
				</ul>
			</nav>
		</ScrollInlineWithSmallButtons>
	);
};
